import {
    faMoneyBill,
    faFileInvoice,
    faFileExport,
    faPaperPlane,
    faBan,
    faTrashAlt
} from '@fortawesome/pro-light-svg-icons';
import { TableCell, TableRow } from '@material-ui/core';
import { PageResult, Period } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Table from '../../Table';
import { useCommonStyles as useTableStyles } from '../../Table/CommonStyles';
import InvoicesHeader from './InvoicesHeader';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons/faEllipsis';
import { ApiClientWrapper } from '@spike/api-client';
import { useApiClientWrapper, usePermissions, useRoles } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import styles from './Invoices.module.scss';
import {
    fetchInvoicesThunk,
    InvoicesStatus,
    cleanInvoice,
    saveDateThunk,
    hasInvoicesThunk,
    voidInvoicesThunk,
    deleteInvoicesThunk,
    issueInvoiceThunk
} from '@spike/invoices-action';
import { InvoiceListRecord, InvoicesSummary } from '@spike/invoice-model';
import { showError, showSuccess } from '@spike/notifications-action';
import InvoiceStatus, { getInvoiceStatusLabel } from './InvoiceStatus';
import Checkout from 'components/Checkout2/Checkout';
import EditInvoiceDateModal from './Edit/EditInvoiceDateModal';
import SendInvoiceModal from './Send/SendInvoiceModal';
import VoidInvoiceModal from './Void/VoidInvoiceModal';
import DeleteInvoiceModal from './Delete/DeleteInvoiceModal';
import IssueInvoiceModal from './Issue/IssueInvoiceModal';
import { PaymentsStatus, sendThunk } from '@spike/payments-action';
import InvoicesEmpty from './InvoicesEmpty';
import InvoiceDetail from './InvoiceDetail';
import { isInvoiceListRecordVoided } from './InvoiceDetail/invoiceUtils';
import { All, draft, getCategoryStatuses } from './InvoicesStatuses';

interface InvoiceExportRecordDto {
    number: number;
    issued_at: string;
    customer_name: string;
    grand_total: string;
    payments_total: number;
    balance: string;
    status: string;
    has_refunds: boolean;
}

interface InvoiceExportResponseDto {
    invoices: Array<InvoiceExportRecordDto>;
}

interface InvoicesProps {
    className?: string;
    clientId?: number;
    emptyState?: ReactNode;
    /** Used when opening Transactions from Commissions "Refunded" without changing the URL. */
    initialCategoryId?: string;
    /** Used to filter transactions to a specific date. */
    initialDate?: Moment;
    /** Used to filter transactions to specific marketplaces. */
    initialMarketplaceIds?: number[];
}

const fetch = (
    apiClientWrapper: ApiClientWrapper,
    dispatch: any,
    marketplaceIds: Array<number>,
    states: Array<string>,
    clientId: number | null,
    period: Period | null,
    page?: number,
    pageSize?: number
) => {
    dispatch(
        fetchInvoicesThunk(
            apiClientWrapper,
            marketplaceIds,
            states,
            page ?? 1,
            pageSize ?? 10,
            clientId ?? undefined,
            period?.from.format('YYYYMMDD') ?? undefined,
            period?.to.format('YYYYMMDD') ?? undefined
        )
    );
};

export const Invoices: FunctionComponent<InvoicesProps> = props => {
    const permissions = usePermissions();
    const { isOwner } = useRoles();
    const tableStyles = useTableStyles();
    const isClientScoped = props.clientId !== undefined;

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { organizationMarketplaces, isUserLoggedInMarketplace, getOrganizationMarketplace } =
        useOrganizationMarketplaces();

    const invoices = useSelector<RootState, PageResult<InvoiceListRecord>>(state => state.invoices.invoices);
    const summary = useSelector<RootState, InvoicesSummary>(state => state.invoices.summary);
    const hasInvoices = useSelector<RootState, boolean | null>(state => state.invoices.hasInvoices);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [loading, setLoading] = useState({ invoices: true, hasInvoices: true });

    const [period, setPeriod] = useState<Period>(() => {
        if (props.initialDate) {
            return { from: moment(props.initialDate.clone()), to: moment(props.initialDate.clone()) };
        }
        return { from: moment(), to: moment() };
    });

    const [clientId, setClientId] = useState<number | null>(props.clientId ?? null);
    const [statuses, setStatuses] = useState<Array<string>>(() => getCategoryStatuses(All.id));
    const [optionSelected, setOptionSelected] = useState<string | undefined>(props.initialDate ? undefined : 'Today');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(
        props.initialMarketplaceIds ?? organizationMarketplaces.map(m => m.id)
    );

    const [showCheckout, setShowCheckout] = useState<{
        orderId?: number;
        bookingId?: number;
        hide: boolean;
    } | null>(null);

    const isCheckoutOpen = showCheckout !== null;

    const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState<{
        invoiceId: number;
        marketplaceId: number;
        marketplaceName: string;
    } | null>(null);
    const returnToInvoiceDetailRef = useRef<{
        invoiceId: number;
        marketplaceId: number;
        marketplaceName: string;
    } | null>(null);
    const [invoiceToEditDate, setInvoiceToEditDate] = useState<InvoiceListRecord | null>(null);
    const [invoiceToSend, setInvoiceToSend] = useState<InvoiceListRecord | null>(null);
    const [invoiceToVoid, setInvoiceToVoid] = useState<InvoiceListRecord | null>(null);
    const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceListRecord | null>(null);
    const [invoiceToIssue, setInvoiceToIssue] = useState<InvoiceListRecord | null>(null);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportRecords, setExportRecords] = useState<Array<Array<string>>>([]);
    const [exportReady, setExportReady] = useState(false);
    const exportRef = useRef<{ export: () => void } | null>(null);
    const pendingDateSaveRef = useRef(false);
    const pendingVoidRef = useRef(false);
    const pendingDeleteRef = useRef(false);
    const pendingIssueRef = useRef(false);

    const savingInvoiceDate = invoicesStatus === InvoicesStatus.SavingDate;
    const voidingInvoice = invoicesStatus === InvoicesStatus.VoidingInvoice;
    const deletingInvoice = invoicesStatus === InvoicesStatus.DeletingInvoice;
    const issuingInvoice = invoicesStatus === InvoicesStatus.IssuingInvoice;

    useEffect(() => {
        if (exportReady) {
            exportRef.current?.export();
            setExportReady(false);
        }
    }, [exportReady]);

    useEffect(() => {
        dispatch(hasInvoicesThunk(apiClientWrapper, marketplaceIds));
    }, []);

    useEffect(() => {
        if (props.clientId !== undefined) {
            setClientId(props.clientId);
        }
    }, [props.clientId]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.FetchInvoicesSuccess:
                setLoading(prev => (prev.invoices ? { ...prev, invoices: false } : prev));
                break;
            case InvoicesStatus.SaveDateSuccess:
                if (pendingDateSaveRef.current) {
                    pendingDateSaveRef.current = false;
                    setInvoiceToEditDate(null);
                    dispatch(showSuccess('Invoice date updated.'));
                    setLoading(prev => ({ ...prev, invoices: true }));
                    fetch(
                        apiClientWrapper,
                        dispatch,
                        marketplaceIds,
                        statuses,
                        clientId,
                        period,
                        invoices.pagination?.page ?? 1,
                        invoices.pagination?.pageSize ?? 10
                    );
                }
                break;
            case InvoicesStatus.VoidInvoiceSuccess:
                if (pendingVoidRef.current) {
                    pendingVoidRef.current = false;
                    setInvoiceToVoid(null);
                    dispatch(showSuccess('Invoice voided.'));
                    setLoading(prev => ({ ...prev, invoices: true }));
                    fetch(
                        apiClientWrapper,
                        dispatch,
                        marketplaceIds,
                        statuses,
                        clientId,
                        period,
                        invoices.pagination?.page ?? 1,
                        invoices.pagination?.pageSize ?? 10
                    );
                }
                break;
            case InvoicesStatus.DeleteInvoiceSuccess:
                if (pendingDeleteRef.current) {
                    pendingDeleteRef.current = false;
                    setInvoiceToDelete(null);
                    dispatch(showSuccess('Invoice deleted.'));
                    setLoading(prev => ({ ...prev, invoices: true }));
                    fetch(
                        apiClientWrapper,
                        dispatch,
                        marketplaceIds,
                        statuses,
                        clientId,
                        period,
                        invoices.pagination?.page ?? 1,
                        invoices.pagination?.pageSize ?? 10
                    );
                }
                break;
            case InvoicesStatus.IssuedInvoiceSuccess:
                if (pendingIssueRef.current) {
                    pendingIssueRef.current = false;
                    setInvoiceToIssue(null);
                    dispatch(showSuccess('Invoice issued.'));
                    setLoading(prev => ({ ...prev, invoices: true }));
                    fetch(
                        apiClientWrapper,
                        dispatch,
                        marketplaceIds,
                        statuses,
                        clientId,
                        period,
                        invoices.pagination?.page ?? 1,
                        invoices.pagination?.pageSize ?? 10
                    );
                }
                break;
            case InvoicesStatus.HasInvoicesSuccess:
                setLoading(prev => (prev.hasInvoices ? { ...prev, hasInvoices: false } : prev));
                fetch(apiClientWrapper, dispatch, marketplaceIds, statuses, clientId, period, 1, 10);
                break;
            case InvoicesStatus.Error:
                if (pendingDateSaveRef.current) {
                    pendingDateSaveRef.current = false;
                } else if (pendingVoidRef.current) {
                    pendingVoidRef.current = false;
                } else if (pendingDeleteRef.current) {
                    pendingDeleteRef.current = false;
                } else if (pendingIssueRef.current) {
                    pendingIssueRef.current = false;
                } else {
                    setLoading(prev =>
                        prev.invoices || prev.hasInvoices ? { ...prev, invoices: false, hasInvoices: false } : prev
                    );
                }
                break;
        }
    }, [invoicesStatus]);

    useNonInitialEffect(() => {
        if (PaymentsStatus.SendSuccess === paymentsStatus) {
            setSendingInvoice(false);
            setInvoiceToSend(null);
        } else if (PaymentsStatus.Error === paymentsStatus && sendingInvoice) {
            setSendingInvoice(false);
        }
    }, [paymentsStatus, sendingInvoice]);

    const changePageHandler = (page: number) => {
        setLoading(prev => ({ ...prev, invoices: true }));
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            statuses,
            clientId,
            period,
            page,
            invoices.pagination?.pageSize ?? 10
        );
    };

    const changePageSizeHandler = (pageSize: number) => {
        setLoading(prev => ({ ...prev, invoices: true }));
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            statuses,
            clientId,
            period,
            invoices.pagination?.page ?? 1,
            pageSize
        );
    };

    const changeStatusesHandler = (statuses: Array<string>) => {
        setLoading(prev => ({ ...prev, invoices: true }));
        setStatuses(statuses);
        fetch(apiClientWrapper, dispatch, marketplaceIds, statuses, clientId, period, 1, 10);
    };

    const changePeriodHandler = (period: Period, opSelect?: string) => {
        setLoading(prev => ({ ...prev, invoices: true }));
        setPeriod(period);
        setOptionSelected(opSelect);
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            statuses,
            clientId,
            period,
            invoices.pagination?.page ?? 1,
            invoices.pagination?.pageSize ?? 10
        );
    };

    const changeClientHandler = (selectedClientId: number | null) => {
        if (clientId !== selectedClientId) {
            setLoading(prev => ({ ...prev, invoices: true }));
            setClientId(selectedClientId);
            fetch(
                apiClientWrapper,
                dispatch,
                marketplaceIds,
                statuses,
                selectedClientId,
                period,
                1,
                invoices.pagination?.pageSize ?? 10
            );
        }
    };

    const changeMarketplaceIdsHandler = (marketplaceIds: Array<number>) => {
        setLoading(prev => ({ ...prev, invoices: true }));

        setMarketplaceIds(marketplaceIds);

        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            statuses,
            props.clientId ?? null,
            period,
            1,
            invoices.pagination?.pageSize ?? 10
        );
        return;
    };

    const exportHandler = useCallback(() => {
        setExporting(true);
        setExportRecords([]);
        setExportReady(false);

        const url = `/invoices?marketplace_id=${apiClientWrapper.marketplaceId}&marketplace_ids=${marketplaceIds.join(
            ','
        )}${clientId === null ? '' : `&customer_id=${clientId}`}&from=${period.from.format(
            'YYYYMMDD'
        )}&to=${period.to.format('YYYYMMDD')}${
            statuses.length > 0 ? `&statuses=${statuses.join(',')}` : ''
        }&page=1&size=9999`;

        apiClientWrapper.apiClient
            .get<InvoiceExportResponseDto>(url, apiClientWrapper.requestConfig)
            .then(response => {
                setExportRecords(
                    response.data.invoices.map(invoice => [
                        String(invoice.number),
                        invoice.customer_name,
                        moment(invoice.issued_at).tz(apiClientWrapper.timeZone).format('MM/DD/YYYY, h:mm a'),
                        `$${Number(invoice.grand_total).toFixed(2)}`,
                        `$${Number(invoice.payments_total).toFixed(2)}`,
                        `$${Number(invoice.balance).toFixed(2)}`,
                        getInvoiceStatusLabel(invoice.status, invoice.has_refunds)
                    ])
                );
                setExporting(false);
                setExportReady(true);
            })
            .catch(() => {
                setExporting(false);
                dispatch(showError('Error exporting invoices.'));
            });
    }, [clientId, marketplaceIds, period, statuses]);

    const checkoutHandler = (orderId: number | undefined, bookingId: number | undefined, _invoiceUuid: string) => {
        if (isCheckoutOpen) {
            return;
        }

        if (!orderId && !bookingId) {
            dispatch(showError('Unable to open checkout for this invoice.'));
            return;
        }

        setShowCheckout({
            orderId: orderId ?? undefined,
            bookingId: bookingId ?? undefined,
            hide: false
        });
    };

    const closeCheckoutHandler = () => {
        const returnToInvoiceDetail = returnToInvoiceDetailRef.current;
        returnToInvoiceDetailRef.current = null;
        setShowCheckout(null);
        setLoading(prev => ({ ...prev, invoices: true }));
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            statuses,
            clientId,
            period,
            invoices.pagination?.page ?? 1,
            invoices.pagination?.pageSize ?? 10
        );

        if (returnToInvoiceDetail) {
            setSelectedInvoiceDetail(returnToInvoiceDetail);
        }
    };

    const openInvoiceDetailHandler = (invoice: InvoiceListRecord) => {
        setSelectedInvoiceDetail({
            invoiceId: invoice.id,
            marketplaceId: invoice.marketplace.id,
            marketplaceName: invoice.marketplace.name
        });
    };

    const closeInvoiceDetailHandler = () => {
        returnToInvoiceDetailRef.current = null;
        setSelectedInvoiceDetail(null);
        dispatch(cleanInvoice());
        setLoading(prev => ({ ...prev, invoices: true }));
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            statuses,
            clientId,
            period,
            invoices.pagination?.page ?? 1,
            invoices.pagination?.pageSize ?? 10
        );
    };

    const checkoutFromInvoiceDetailHandler = (
        bookingId: number | undefined,
        orderId: number | undefined,
        invoiceUuid: string
    ) => {
        if (!selectedInvoiceDetail) {
            return;
        }

        returnToInvoiceDetailRef.current = selectedInvoiceDetail;
        setSelectedInvoiceDetail(null);
        dispatch(cleanInvoice());

        checkoutHandler(orderId, bookingId, invoiceUuid);
    };

    const openSendInvoiceHandler = (invoice: InvoiceListRecord) => {
        setInvoiceToSend(invoice);
    };

    const closeSendInvoiceHandler = () => {
        if (!sendingInvoice) {
            setInvoiceToSend(null);
        }
    };

    const sendInvoiceHandler = useCallback(
        (email: string) => {
            if (!invoiceToSend) {
                return;
            }

            setSendingInvoice(true);
            dispatch(sendThunk(apiClientWrapper, invoiceToSend.id, true, false, false, email));
        },
        [apiClientWrapper, dispatch, invoiceToSend]
    );

    const openEditInvoiceDateHandler = (invoice: InvoiceListRecord) => {
        setInvoiceToEditDate(invoice);
    };

    const closeEditInvoiceDateHandler = () => {
        if (!savingInvoiceDate) {
            setInvoiceToEditDate(null);
        }
    };

    const saveInvoiceDateHandler = useCallback(
        (date: Moment) => {
            if (!invoiceToEditDate) {
                return;
            }

            pendingDateSaveRef.current = true;
            dispatch(saveDateThunk(apiClientWrapper, invoiceToEditDate.id, date.format('YYYY-MM-DD HH:mm:ss')));
        },
        [apiClientWrapper, dispatch, invoiceToEditDate]
    );

    const openVoidInvoiceHandler = (invoice: InvoiceListRecord) => {
        setInvoiceToVoid(invoice);
    };

    const closeVoidInvoiceHandler = () => {
        if (!voidingInvoice) {
            setInvoiceToVoid(null);
        }
    };

    const voidInvoiceHandler = useCallback(() => {
        if (!invoiceToVoid) {
            return;
        }

        pendingVoidRef.current = true;
        dispatch(voidInvoicesThunk(apiClientWrapper, invoiceToVoid.id));
    }, [apiClientWrapper, dispatch, invoiceToVoid]);

    const openDeleteInvoiceHandler = (invoice: InvoiceListRecord) => {
        setInvoiceToDelete(invoice);
    };

    const closeDeleteInvoiceHandler = () => {
        if (!deletingInvoice) {
            setInvoiceToDelete(null);
        }
    };

    const deleteInvoiceHandler = useCallback(() => {
        if (!invoiceToDelete) {
            return;
        }

        pendingDeleteRef.current = true;
        dispatch(deleteInvoicesThunk(apiClientWrapper, invoiceToDelete.id));
    }, [apiClientWrapper, dispatch, invoiceToDelete]);

    const openIssueInvoiceHandler = (invoice: InvoiceListRecord) => {
        setInvoiceToIssue(invoice);
    };

    const closeIssueInvoiceHandler = () => {
        if (!issuingInvoice) {
            setInvoiceToIssue(null);
        }
    };

    const issueInvoiceHandler = useCallback(() => {
        if (!invoiceToIssue || pendingIssueRef.current) {
            return;
        }

        pendingIssueRef.current = true;
        dispatch(issueInvoiceThunk(apiClientWrapper, invoiceToIssue.id));
    }, [apiClientWrapper, dispatch, invoiceToIssue]);

    const headers = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.invoiceColumn)}>
                Invoice
            </TableCell>
            {!isClientScoped && (
                <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Client</TableCell>
            )}
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.dateColumn)}>
                Date
            </TableCell>
            <TableCell
                align="right"
                className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.amountColumn)}
            >
                Total
            </TableCell>
            <TableCell
                align="right"
                className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.amountColumn)}
            >
                Paid
            </TableCell>
            <TableCell
                align="right"
                className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.amountColumn)}
            >
                Remaining
            </TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.statusColumn)}>
                Status
            </TableCell>
            <TableCell
                align="center"
                className={clsx(tableStyles.headerCell, tableStyles.darkCell, styles.actionsColumn)}
            />
        </>
    );

    const getLocation = useCallback(
        (marketplaceId: number) =>
            organizationMarketplaces.length > 1 ? (
                <div className={styles.locationContainer}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.locationIcon}
                    />
                    {getOrganizationMarketplace(marketplaceId)?.name}
                </div>
            ) : null,
        [organizationMarketplaces, getOrganizationMarketplace]
    );

    const rows = (
        <>
            {invoices.page.map(invoice => (
                <TableRow
                    key={invoice.uuid}
                    onClick={() => !isCheckoutOpen && openInvoiceDetailHandler(invoice)}
                    style={{ cursor: isCheckoutOpen ? 'default' : 'pointer' }}
                >
                    <TableCell className={clsx(tableStyles.cell, styles.invoiceColumn)}>{invoice.number}</TableCell>

                    {!isClientScoped && <TableCell className={tableStyles.cell}>{invoice.customer.name}</TableCell>}

                    <TableCell className={clsx(tableStyles.cell, styles.dateColumn)}>
                        <div className={styles.dateCell}>
                            <div className={styles.dateCellContent}>
                                {invoice.date.format('MM/DD/YYYY, h:mm a')}
                                {getLocation(invoice.marketplace.id)}
                            </div>
                            {isOwner && (
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    className={styles.dateEditIcon}
                                    onClick={event => {
                                        event.stopPropagation();
                                        openEditInvoiceDateHandler(invoice);
                                    }}
                                />
                            )}
                        </div>
                    </TableCell>

                    <TableCell
                        align="right"
                        className={clsx(tableStyles.cell, styles.amountColumn)}
                    >{`$${invoice.grandTotal.toFixed(2)}`}</TableCell>

                    <TableCell
                        align="right"
                        className={clsx(tableStyles.cell, styles.amountColumn)}
                    >{`$${invoice.netPaidTotal.toFixed(2)}`}</TableCell>

                    <TableCell
                        align="right"
                        className={clsx(tableStyles.cell, styles.amountColumn)}
                    >{`$${invoice.balance.toFixed(2)}`}</TableCell>

                    <TableCell className={clsx(tableStyles.cell, styles.statusColumn)}>
                        <InvoiceStatus
                            invoiceStatus={invoice.status}
                            hasRefunds={invoice.hasRefunds}
                        />
                    </TableCell>

                    <TableCell
                        align="center"
                        className={clsx(tableStyles.cell, styles.actionsColumn)}
                    >
                        {isUserLoggedInMarketplace(invoice.marketplace.id) && (
                            <div
                                className={styles.actionsDropdown}
                                onClick={event => event.stopPropagation()}
                            >
                                <Dropdown>
                                    <DropdownTrigger
                                        className={styles.actionsDropdownTrigger}
                                        disabled={isCheckoutOpen}
                                    >
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </DropdownTrigger>
                                    <DropdownContent
                                        align="end"
                                        sideOffset={4}
                                        minWidth={invoice.status === draft ? 190 : 150}
                                        zIndex={1300}
                                        showBottomModalOnMobile={false}
                                    >
                                        {invoice.status === draft ? (
                                            <>
                                                <DropdownItem
                                                    leftIcon={faFileInvoice}
                                                    disabled={isCheckoutOpen}
                                                    onSelect={() => openInvoiceDetailHandler(invoice)}
                                                >
                                                    View Invoice
                                                </DropdownItem>
                                                <DropdownItem
                                                    leftIcon={faFileExport}
                                                    disabled={isCheckoutOpen}
                                                    onSelect={() => openIssueInvoiceHandler(invoice)}
                                                >
                                                    Issue Invoice Now
                                                </DropdownItem>
                                                <DropdownItem
                                                    leftIcon={faTrashAlt}
                                                    disabled={isCheckoutOpen}
                                                    className={styles.destructiveAction}
                                                    onSelect={() => openDeleteInvoiceHandler(invoice)}
                                                >
                                                    Delete Draft
                                                </DropdownItem>
                                            </>
                                        ) : (
                                            <>
                                                {permissions.checkout.takePayments && invoice.balance > 0 && (
                                                    <DropdownItem
                                                        leftIcon={faMoneyBill}
                                                        disabled={isCheckoutOpen}
                                                        onSelect={() =>
                                                            checkoutHandler(
                                                                invoice.orderId ?? undefined,
                                                                invoice.bookingId,
                                                                invoice.uuid
                                                            )
                                                        }
                                                    >
                                                        Checkout
                                                    </DropdownItem>
                                                )}
                                                <DropdownItem
                                                    leftIcon={faFileInvoice}
                                                    disabled={isCheckoutOpen}
                                                    onSelect={() => openInvoiceDetailHandler(invoice)}
                                                >
                                                    View Invoice
                                                </DropdownItem>
                                                {!isInvoiceListRecordVoided(invoice) && (
                                                    <DropdownItem
                                                        leftIcon={faPaperPlane}
                                                        disabled={isCheckoutOpen}
                                                        onSelect={() => openSendInvoiceHandler(invoice)}
                                                    >
                                                        Send Invoice
                                                    </DropdownItem>
                                                )}
                                                {invoice.voidable && (
                                                    <DropdownItem
                                                        leftIcon={faBan}
                                                        disabled={isCheckoutOpen}
                                                        className={styles.destructiveAction}
                                                        onSelect={() => openVoidInvoiceHandler(invoice)}
                                                    >
                                                        Void
                                                    </DropdownItem>
                                                )}
                                                {isOwner && invoice.destroyable && (
                                                    <DropdownItem
                                                        leftIcon={faTrashAlt}
                                                        disabled={isCheckoutOpen}
                                                        className={styles.destructiveAction}
                                                        onSelect={() => openDeleteInvoiceHandler(invoice)}
                                                    >
                                                        Delete
                                                    </DropdownItem>
                                                )}
                                            </>
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </>
    );

    const currentPagination = invoices.pagination;
    const currentPageItems = invoices.page;
    const isCurrentListEmpty =
        !loading.invoices && (currentPagination?.totalRecords || 0) === 0 && currentPageItems.length === 0;

    return (
        <>
            {loading.hasInvoices && (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            )}
            {!loading.hasInvoices && !hasInvoices && <InvoicesEmpty />}
            {!loading.hasInvoices && hasInvoices && (
                <div className={clsx(styles.container, props.className)}>
                    <InvoicesHeader
                        clientId={clientId}
                        hideClientFilter={isClientScoped}
                        period={period!}
                        optionSelected={optionSelected}
                        initialMarketplaceIds={marketplaceIds}
                        onChangeStatuses={changeStatusesHandler}
                        onChangePeriod={changePeriodHandler}
                        onChangeClient={changeClientHandler}
                        onChangeMarketplaceIds={changeMarketplaceIdsHandler}
                        totalElements={currentPagination?.totalRecords}
                        summary={{
                            totalOutstandingAmount: summary.totalOutstanding,
                            invoicesCount: summary.invoicesCount,
                            clientsWithDebtCount: summary.clientsWithDebtCount,
                            loading: loading.invoices
                        }}
                        exporting={exporting}
                        exportRef={exportRef}
                        exportRecords={exportRecords}
                        exportFilename={`invoice_report_${period.from.format('YYYY-MM-DD')}_${period.to.format(
                            'YYYY-MM-DD'
                        )}.csv`}
                        onExport={exportHandler}
                    />
                    {isClientScoped && isCurrentListEmpty && props.emptyState ? (
                        props.emptyState
                    ) : (
                        <div className={styles.tableSection}>
                            <Table
                                headers={headers}
                                rows={loading.invoices ? <></> : rows}
                                pagination={loading.invoices ? undefined : invoices.pagination ?? undefined}
                                loading={loading.invoices}
                                onChangePage={changePageHandler}
                                onChangePageSize={changePageSizeHandler}
                            />
                        </div>
                    )}
                </div>
            )}
            {showCheckout && (
                <Checkout
                    orderId={showCheckout.orderId}
                    bookingId={showCheckout.bookingId}
                    onClose={closeCheckoutHandler}
                    onPayByLinkSent={closeCheckoutHandler}
                    onBack={closeCheckoutHandler}
                    hide={showCheckout?.hide}
                    zIndex={props.clientId ? 10100 : undefined}
                />
            )}

            {selectedInvoiceDetail && !isCheckoutOpen && (
                <InvoiceDetail
                    invoiceId={selectedInvoiceDetail.invoiceId}
                    marketplaceId={selectedInvoiceDetail.marketplaceId}
                    marketplaceName={selectedInvoiceDetail.marketplaceName}
                    onClose={closeInvoiceDetailHandler}
                    onCheckout={checkoutFromInvoiceDetailHandler}
                    zIndex={props.clientId ? 10100 : undefined}
                />
            )}

            <EditInvoiceDateModal
                invoice={invoiceToEditDate}
                saving={savingInvoiceDate}
                onClose={closeEditInvoiceDateHandler}
                onSave={saveInvoiceDateHandler}
            />

            <SendInvoiceModal
                invoice={invoiceToSend}
                sending={sendingInvoice}
                onClose={closeSendInvoiceHandler}
                onSend={sendInvoiceHandler}
            />

            <VoidInvoiceModal
                invoice={invoiceToVoid}
                voiding={voidingInvoice}
                onClose={closeVoidInvoiceHandler}
                onConfirm={voidInvoiceHandler}
            />

            <DeleteInvoiceModal
                invoice={invoiceToDelete}
                deleting={deletingInvoice}
                onClose={closeDeleteInvoiceHandler}
                onConfirm={deleteInvoiceHandler}
            />

            <IssueInvoiceModal
                invoice={invoiceToIssue}
                issuing={issuingInvoice}
                onClose={closeIssueInvoiceHandler}
                onConfirm={issueInvoiceHandler}
            />
        </>
    );
};

export default Invoices;
